import { request } from '@/lib/api'

/**
 * Обёртки над `/api/v1/warehouse/topology` — склады, зоны, ячейки и генератор ячеек.
 *
 * Сервер отдаёт JSON с `non_null`: незаполненного поля в ответе нет вовсе, поэтому
 * необязательные поля здесь объявлены `?`, а не `| null`.
 */

export type PickingStrategy = 'FIFO' | 'FEFO' | 'LIFO' | 'MANUAL'

export interface StorageModeOption {
  code: string
  /** Режим хранит товар по ячейкам: только у такой зоны есть генератор и таблица ячеек. */
  usesCells: boolean
}

export interface RegimeOption {
  code: string
  defaultMinC?: number
  defaultMaxC?: number
}

export interface OrganizationOption {
  id: string
  name: string
  inn?: string
}

export interface WarehouseRow {
  id: string
  version: number
  code: string
  name: string
  address?: string
  organizationId: string
  organizationName?: string
  managerEmployeeId?: string
  managerName?: string
  defaultPickingStrategy: PickingStrategy
  allowsCrossDocking: boolean
  active: boolean
  zones: number
  cells: number
  occupiedCells: number
  blockedCells: number
  /**
   * Что вызывающий может сделать с этим складом — решает сервер (область видимости по
   * заведующему, V59), экран правило не повторяет: правка, удаление, новые зоны и ячейки.
   */
  editable: boolean
  deletable: boolean
  extendable: boolean
}

export interface WarehouseList {
  warehouses: WarehouseRow[]
  strategies: PickingStrategy[]
  storageModes: StorageModeOption[]
  temperatureRegimes: RegimeOption[]
  organizations: OrganizationOption[]
  defaultMask: string
  maxCells: number
  /** Заводить склады может только неограниченное право: иначе заведующий назначил бы себе любой. */
  mayCreateWarehouse: boolean
  /** Менять заведующего — тоже только неограниченное право. */
  mayAssignManager: boolean
}

export interface ZoneRow {
  id: string
  version: number
  code: string
  name: string
  storageMode: string
  usesCells: boolean
  temperatureRegime: string
  temperatureMinC?: number
  temperatureMaxC?: number
  /** Своя стратегия зоны; нет поля — наследует склад. */
  pickingStrategy?: PickingStrategy
  effectivePickingStrategy: PickingStrategy
  priority: number
  ownerCounterpartyId?: string
  ownerName?: string
  active: boolean
  cells: number
  occupiedCells: number
  blockedCells: number
}

export interface WarehouseDetail {
  warehouse: WarehouseRow
  zones: ZoneRow[]
}

export interface CellRow {
  id: string
  version: number
  code: string
  rowNo?: string
  rackNo?: string
  tierNo?: string
  positionNo?: string
  barcode?: string
  maxWeightKg?: number
  maxVolumeM3?: number
  maxPallets?: number
  lengthMm?: number
  widthMm?: number
  heightMm?: number
  abcClass?: 'A' | 'B' | 'C'
  pickable: boolean
  blocked: boolean
  blockReason?: string
  hasStock: boolean
  hasReservation: boolean
  hasOpenTask: boolean
}

export interface CellPage {
  cells: CellRow[]
  total: number
  truncated: boolean
  rows: string[]
}

export interface WarehouseInput {
  code: string
  name: string
  organizationId: string
  address: string | null
  managerEmployeeId: string | null
  defaultPickingStrategy: PickingStrategy
  allowsCrossDocking: boolean
  active: boolean
  version?: number
}

export interface ZoneInput {
  code: string
  name: string
  storageMode: string
  temperatureRegime: string
  temperatureMinC: number | null
  temperatureMaxC: number | null
  /** Пусто — наследовать стратегию склада. */
  pickingStrategy: string
  priority: number
  ownerCounterpartyId: string | null
  active: boolean
  version?: number
}

export interface CellLimits {
  maxWeightKg: number | null
  maxVolumeM3: number | null
  maxPallets: number | null
  lengthMm: number | null
  widthMm: number | null
  heightMm: number | null
  abcClass: string | null
  pickable: boolean
}

export interface CellTemplateInput extends CellLimits {
  rowFrom: string
  rowTo: string
  rackFrom: string
  rackTo: string
  tierFrom: string
  tierTo: string
  positionFrom: string
  positionTo: string
  mask: string
}

export interface CellPreview {
  mask: string
  total: number
  toCreate: number
  existing: number
  first: string[]
  last: string[]
  existingCodes: string[]
}

export interface CellGenerated {
  created: number
  skipped: number
  plan: CellPreview
}

export interface CellFilter {
  row?: string
  blocked?: boolean
  occupied?: boolean
  q?: string
}

const BASE = '/warehouse/topology'

export const fetchWarehouses = (signal?: AbortSignal) =>
  request<WarehouseList>(`${BASE}/warehouses`, { signal })

export const fetchWarehouse = (id: string, signal?: AbortSignal) =>
  request<WarehouseDetail>(`${BASE}/warehouses/${id}`, { signal })

export const createWarehouse = (body: WarehouseInput) =>
  request<WarehouseRow>(`${BASE}/warehouses`, { method: 'POST', body })

export const updateWarehouse = (id: string, body: WarehouseInput) =>
  request<WarehouseRow>(`${BASE}/warehouses/${id}`, { method: 'PUT', body })

export const deleteWarehouse = (id: string) =>
  request<void>(`${BASE}/warehouses/${id}`, { method: 'DELETE' })

export const createZone = (warehouseId: string, body: ZoneInput) =>
  request<ZoneRow>(`${BASE}/warehouses/${warehouseId}/zones`, { method: 'POST', body })

export const updateZone = (zoneId: string, body: ZoneInput) =>
  request<ZoneRow>(`${BASE}/zones/${zoneId}`, { method: 'PUT', body })

export const deleteZone = (zoneId: string) =>
  request<void>(`${BASE}/zones/${zoneId}`, { method: 'DELETE' })

export function fetchCells(zoneId: string, filter: CellFilter, signal?: AbortSignal) {
  const params = new URLSearchParams()
  if (filter.row) params.set('row', filter.row)
  if (filter.blocked !== undefined) params.set('blocked', String(filter.blocked))
  if (filter.occupied !== undefined) params.set('occupied', String(filter.occupied))
  if (filter.q) params.set('q', filter.q)
  const query = params.toString()
  return request<CellPage>(`${BASE}/zones/${zoneId}/cells${query ? `?${query}` : ''}`, { signal })
}

export const previewCells = (zoneId: string, body: CellTemplateInput) =>
  request<CellPreview>(`${BASE}/zones/${zoneId}/cells/preview`, { method: 'POST', body })

export const generateCells = (zoneId: string, body: CellTemplateInput) =>
  request<CellGenerated>(`${BASE}/zones/${zoneId}/cells`, { method: 'POST', body })

export const blockCells = (zoneId: string, cellIds: string[], reason: string) =>
  request<{ changed: number }>(`${BASE}/zones/${zoneId}/cells/block`, { method: 'POST', body: { cellIds, reason } })

export const unblockCells = (zoneId: string, cellIds: string[]) =>
  request<{ changed: number }>(`${BASE}/zones/${zoneId}/cells/unblock`, { method: 'POST', body: { cellIds } })

export const updateCell = (cellId: string, body: CellLimits & { version?: number }) =>
  request<CellRow>(`${BASE}/cells/${cellId}`, { method: 'PUT', body })

export const deleteCell = (cellId: string) =>
  request<void>(`${BASE}/cells/${cellId}`, { method: 'DELETE' })

/** Строка подбора: только то, что нужно, чтобы узнать запись, — без карточки. */
export interface PickOption {
  id: string
  name: string
  /** Должность сотрудника или ИНН контрагента. */
  extra?: string
}

interface EmployeePick { id: string; name: string; position?: string }
interface CounterpartyPick { id: string; name: string; inn?: string }

/**
 * Подбор заведующего и владельца зоны — узкие эндпоинты топологии, а не механизм списков:
 * у заведующего складом нет права на `hr.employee` и `sales.counterparty`, и общий поиск
 * отвечал ему «недоступно».
 */
export async function pickEmployees(q: string, signal?: AbortSignal): Promise<PickOption[]> {
  const body = await request<{ items: EmployeePick[] }>(`${BASE}/pick/employees?q=${encodeURIComponent(q)}`, { signal })
  return body.items.map((item) => ({ id: item.id, name: item.name, extra: item.position }))
}

export async function pickCounterparties(q: string, signal?: AbortSignal): Promise<PickOption[]> {
  const body = await request<{ items: CounterpartyPick[] }>(`${BASE}/pick/counterparties?q=${encodeURIComponent(q)}`, { signal })
  return body.items.map((item) => ({ id: item.id, name: item.name, extra: item.inn }))
}
